import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import SignaturePad from '../components/SignaturePad';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus, PencilSimple, Trash, Toolbox, CheckCircle, Clock, MapPin, Phone, EnvelopeSimple,
  User as UserIcon, FilePdf, Signature, Camera, Printer, Receipt, Image as ImageIcon, Prohibit,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { downloadBlob } from '../utils/download';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  client_id: '',
  invoice_id: '',
  technician_id: '',
  scheduled_date: '',
  request_description: '',
  observation: '',
  done: false,
};

const Interventions = () => {
  const confirm = useConfirm();
  const { user } = useAuth();
  const isTech = user?.role === 'technician';
  const isSeller = user?.role === 'seller';
  const isAdmin = user?.role === 'admin';

  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [cityFilter, setCityFilter] = useState([]);  // selected cities (empty = all)
  const [showCityFilter, setShowCityFilter] = useState(false);

  const [signDialog, setSignDialog] = useState({ open: false, intervention: null, signer: 'client' });
  const [photoDialog, setPhotoDialog] = useState({ open: false, intervention: null });
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    fetchAll();
  }, []);

  // Pre-fill creation form when navigating from invoice list
  useEffect(() => {
    if (isTech) return;
    const invoiceId = searchParams.get('invoice_id');
    const clientId = searchParams.get('client_id');
    if (invoiceId || clientId) {
      setForm((f) => ({ ...f, invoice_id: invoiceId || '', client_id: clientId || f.client_id }));
      setShowDialog(true);
    }
  }, [searchParams, isTech]);

  // When invoice_id is set, fetch invoice and pre-fill description with its service items
  useEffect(() => {
    if (isTech) return;
    const id = form.invoice_id;
    if (!id || editing) return;  // do not overwrite description when editing existing intervention
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/invoices/${id}`, { withCredentials: true });
        if (cancelled) return;
        const items = (r.data.items || []).filter(it => it.product_type === 'service');
        if (items.length === 0) return;
        const lines = items.map(it => `• ${it.description} (×${it.quantity})`).join('\n');
        const header = `Prestations facturées (Facture ${r.data.invoice_number}) :\n${lines}`;
        setForm((f) => ({
          ...f,
          // only set if current description is empty or starts with previous header (replace cleanly)
          request_description: f.request_description && !f.request_description.startsWith('Prestations facturées')
            ? `${header}\n\n${f.request_description}`
            : header,
        }));
      } catch (e) {
        // silent — invoice may have been deleted
      }
    })();
    return () => { cancelled = true; };
  }, [form.invoice_id, editing, isTech]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const requests = [axios.get(`${API_URL}/api/interventions`, { withCredentials: true })];
      if (!isTech) {
        requests.push(axios.get(`${API_URL}/api/clients`, { withCredentials: true }));
        requests.push(axios.get(`${API_URL}/api/technicians`, { withCredentials: true }));
      }
      const responses = await Promise.all(requests);
      setItems(responses[0].data);
      if (!isTech) {
        setClients(responses[1].data);
        setTechnicians(responses[2].data);
      }
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditing(null);
  };

  const openCreate = () => { resetForm(); setShowDialog(true); };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      client_id: it.client_id || '',
      invoice_id: it.invoice_id || '',
      technician_id: it.technician_id || '',
      scheduled_date: it.scheduled_date ? it.scheduled_date.slice(0, 16) : '',
      request_description: it.request_description || '',
      observation: it.observation || '',
      done: !!it.done,
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        scheduled_date: form.scheduled_date ? new Date(form.scheduled_date).toISOString() : null,
        technician_id: form.technician_id || null,
        invoice_id: form.invoice_id || null,
      };
      if (editing) {
        await axios.put(`${API_URL}/api/interventions/${editing.id}`, payload, { withCredentials: true });
        toast.success('Intervention mise à jour');
      } else {
        await axios.post(`${API_URL}/api/interventions`, payload, { withCredentials: true });
        toast.success('Intervention créée');
      }
      setShowDialog(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const toggleDone = async (it) => {
    if (isSeller) {
      toast.error('Seul un administrateur ou un technicien peut marquer une intervention comme effectuée');
      return;
    }
    const newDone = !it.done;
    if (newDone && !(await confirm({ title: 'Marquer comme effectuée ?', description: 'Confirmez-vous que cette intervention a été réalisée ?', confirmText: 'Oui, effectuée' }))) return;
    try {
      await axios.put(`${API_URL}/api/interventions/${it.id}`, { done: newDone }, { withCredentials: true });
      toast.success(newDone ? 'Intervention marquée comme effectuée' : 'Intervention rouverte');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (it) => {
    if (!(await confirm({ title: 'Supprimer cette intervention ?', confirmText: 'Supprimer', destructive: true }))) return;
    try {
      await axios.delete(`${API_URL}/api/interventions/${it.id}`, { withCredentials: true });
      toast.success('Intervention supprimée');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const downloadBon = async (it) => {
    try {
      const r = await axios.get(`${API_URL}/api/interventions/${it.id}/bon-intervention/pdf`, { withCredentials: true, responseType: 'blob' });
      downloadBlob(r.data, `bon_intervention_${it.id}.pdf`, 'application/pdf');
      toast.success('Bon téléchargé');
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const printBon = async (it) => {
    try {
      const r = await axios.get(`${API_URL}/api/interventions/${it.id}/bon-intervention/pdf`, { withCredentials: true, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(r.data);
      const w = window.open(blobUrl, '_blank');
      if (!w) {
        // Fallback if popup blocked
        downloadBlob(r.data, `bon_intervention_${it.id}.pdf`, 'application/pdf');
        toast.info('Pop-up bloqué : téléchargement à la place');
      } else {
        setTimeout(() => {
          try { w.print(); } catch (_) { /* ignore */ }
        }, 600);
      }
    } catch (err) {
      toast.error("Erreur lors de l'impression");
    }
  };

  const saveSignature = async (dataUrl) => {
    const { intervention, signer } = signDialog;
    try {
      await axios.post(`${API_URL}/api/interventions/${intervention.id}/sign`, { signer, signature: dataUrl }, { withCredentials: true });
      toast.success(signer === 'client' ? 'Signature client enregistrée' : 'Signature technicien enregistrée');
      setSignDialog({ open: false, intervention: null, signer: 'client' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const openPhoto = (it) => {
    setPhotoDialog({ open: true, intervention: it });
  };

  const onPhotoSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo trop volumineuse (max 5 Mo)');
      return;
    }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await axios.post(`${API_URL}/api/interventions/${photoDialog.intervention.id}/photo`, { photo: ev.target.result }, { withCredentials: true });
        toast.success('Photo enregistrée');
        setPhotoDialog({ open: false, intervention: null });
        fetchAll();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erreur');
      }
    };
    reader.readAsDataURL(file);
    // Clear input to allow re-select
    e.target.value = '';
  };

  const extractCity = (addr) => {
    if (!addr) return '';
    const last = addr.split('\n').slice(-1)[0] || addr;
    const m = last.match(/\d{5}\s+(.+)/);
    return (m ? m[1] : last).trim();
  };

  const availableCities = useMemo(() => {
    const set = new Set();
    items.forEach((it) => {
      const c = extractCity(it.client_address);
      if (c) set.add(c);
    });
    return Array.from(set).sort();
  }, [items]);

  const toggleCity = (city) => {
    setCityFilter((prev) => (prev.includes(city) ? prev.filter((c) => c !== city) : [...prev, city]));
  };

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const arr = items.filter((it) => {
      if (filterStatus === 'pending' && (it.done || it.cancelled)) return false;
      if (filterStatus === 'done' && !it.done) return false;
      if (filterStatus === 'cancelled' && !it.cancelled) return false;
      if (cityFilter.length > 0) {
        const c = extractCity(it.client_address);
        if (!cityFilter.includes(c)) return false;
      }
      if (!q) return true;
      return (
        (it.client_name || '').toLowerCase().includes(q) ||
        (it.technician_name || '').toLowerCase().includes(q) ||
        (it.request_description || '').toLowerCase().includes(q) ||
        (it.invoice_number || '').toLowerCase().includes(q) ||
        (it.client_address || '').toLowerCase().includes(q)
      );
    });
    const sorted = [...arr];
    if (sortBy === 'city') {
      sorted.sort((a, b) => extractCity(a.client_address).toLowerCase().localeCompare(extractCity(b.client_address).toLowerCase()));
    } else if (sortBy === 'date_asc') {
      sorted.sort((a, b) => new Date(a.scheduled_date || 0) - new Date(b.scheduled_date || 0));
    } else {
      sorted.sort((a, b) => new Date(b.scheduled_date || 0) - new Date(a.scheduled_date || 0));
    }
    return sorted;
  }, [items, searchQuery, filterStatus, sortBy, cityFilter]);

  const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="interventions-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Gestion des interventions</h1>
            <p className="text-slate-600">{isTech ? 'Vos interventions assignées' : 'Planifiez, suivez et historisez vos interventions'}</p>
          </div>
          {!isTech && (
            <Button onClick={openCreate} data-testid="create-intervention-btn" className="bg-orange-500 hover:bg-orange-400 text-white">
              <Plus size={20} weight="bold" className="mr-2" />
              Nouvelle intervention
            </Button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-col md:flex-row gap-3">
          <Input
            placeholder="Rechercher (client, technicien, description, n° facture)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-interventions-input"
            className="flex-1"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="filter-interventions-status"
            className="px-3 py-2 border border-slate-300 rounded-md bg-white text-sm"
          >
            <option value="all">Toutes</option>
            <option value="pending">À effectuer</option>
            <option value="done">Effectuées</option>
            <option value="cancelled">Annulées</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="sort-interventions"
            className="px-3 py-2 border border-slate-300 rounded-md bg-white text-sm"
          >
            <option value="date_desc">Date ↓ (plus récente)</option>
            <option value="date_asc">Date ↑ (plus ancienne)</option>
            <option value="city">Ville / Commune (A→Z)</option>
          </select>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCityFilter(!showCityFilter)}
              data-testid="city-filter-toggle"
              className="px-3 py-2 border border-slate-300 rounded-md bg-white text-sm hover:bg-slate-50"
            >
              Filtrer par ville {cityFilter.length > 0 && <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs bg-blue-600 text-white">{cityFilter.length}</span>}
            </button>
            {showCityFilter && (
              <div data-testid="city-filter-panel" className="absolute z-10 mt-2 right-0 w-72 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-semibold text-slate-700">Villes / Communes</span>
                  {cityFilter.length > 0 && (
                    <button type="button" onClick={() => setCityFilter([])} data-testid="city-filter-clear" className="text-xs text-blue-600 hover:underline">Tout désélectionner</button>
                  )}
                </div>
                {availableCities.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Aucune ville disponible</p>
                ) : (
                  availableCities.map(city => (
                    <label key={city} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 px-1 rounded">
                      <input
                        type="checkbox"
                        checked={cityFilter.includes(city)}
                        onChange={() => toggleCity(city)}
                        data-testid={`city-filter-${city.replace(/\s+/g, '-')}`}
                      />
                      <span className="text-sm text-slate-700">{city}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">
            <Toolbox size={48} className="mx-auto text-slate-300 mb-3" />
            Aucune intervention
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((it) => (
              <div
                key={it.id}
                data-testid={`intervention-card-${it.id}`}
                className={`bg-white border-l-4 ${it.cancelled ? 'border-l-red-500 opacity-75' : (it.done ? 'border-l-green-500' : 'border-l-orange-500')} border border-slate-200 rounded-lg p-5 shadow-sm`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {it.cancelled ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700" data-testid={`intervention-cancelled-badge-${it.id}`}>
                          <Prohibit size={14} weight="fill" /> Annulée
                        </span>
                      ) : it.done ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          <CheckCircle size={14} weight="fill" /> Effectuée
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                          <Clock size={14} weight="fill" /> En attente
                        </span>
                      )}
                      <span className="text-xs text-slate-500">{it.scheduled_date ? `Prévue: ${fmtDate(it.scheduled_date)}` : 'Date non planifiée'}</span>
                      {it.invoice_number && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                          <Receipt size={12} /> Facture {it.invoice_number}
                        </span>
                      )}
                      {it.client_signature && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700">
                          <Signature size={12} /> Client signé
                        </span>
                      )}
                      {it.technician_signature && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-violet-50 text-violet-700">
                          <Signature size={12} /> Tech. signé
                        </span>
                      )}
                      {it.signed_photo && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-50 text-amber-700">
                          <ImageIcon size={12} /> Photo
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{it.client_name || '—'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-700 mb-3">
                      {it.client_address && (<div className="flex items-start gap-2"><MapPin size={16} className="text-slate-400 mt-0.5" /><span>{it.client_address}</span></div>)}
                      {it.client_phone && (<div className="flex items-center gap-2"><Phone size={16} className="text-slate-400" /><a href={`tel:${it.client_phone}`} className="text-blue-600 hover:underline">{it.client_phone}</a></div>)}
                      {it.client_email && (<div className="flex items-center gap-2"><EnvelopeSimple size={16} className="text-slate-400" /><a href={`mailto:${it.client_email}`} className="text-blue-600 hover:underline">{it.client_email}</a></div>)}
                      {it.technician_name && (<div className="flex items-center gap-2"><UserIcon size={16} className="text-slate-400" /><span>Technicien: <strong>{it.technician_name}</strong></span></div>)}
                    </div>
                    {it.request_description && (<div className="text-sm text-slate-700 mb-2"><strong>Demande :</strong> {it.request_description}</div>)}
                    {it.observation && (<div className="text-sm text-slate-600 italic bg-slate-50 p-2 rounded"><strong>Observation :</strong> {it.observation}</div>)}
                    {it.done && it.done_at && (<div className="text-xs text-green-700 mt-2">Validée le {fmtDate(it.done_at)} par {it.done_by}</div>)}
                    {it.cancelled && (<div className="text-xs text-red-700 mt-2">Annulée le {fmtDate(it.cancelled_at)} — {it.cancelled_reason || 'Motif non précisé'}</div>)}
                  </div>
                  <div className="flex flex-wrap md:flex-col gap-2 md:max-w-xs">
                    {!isSeller && (
                      <Button onClick={() => toggleDone(it)} data-testid={`toggle-done-${it.id}`} className={it.done ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-green-600 hover:bg-green-500 text-white'}>
                        <CheckCircle size={16} className="mr-1" />
                        {it.done ? 'Rouvrir' : 'Marquer effectuée'}
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => downloadBon(it)} data-testid={`download-bon-${it.id}`}>
                      <FilePdf size={16} className="mr-1" /> Bon (PDF)
                    </Button>
                    {!isSeller && (
                      <Button variant="outline" onClick={() => printBon(it)} data-testid={`print-bon-${it.id}`}>
                        <Printer size={16} className="mr-1" /> Imprimer
                      </Button>
                    )}
                    {!isSeller && (
                      <Button variant="outline" onClick={() => setSignDialog({ open: true, intervention: it, signer: 'client' })} data-testid={`sign-client-${it.id}`} className="text-emerald-700">
                        <Signature size={16} className="mr-1" /> Signer (client)
                      </Button>
                    )}
                    {!isSeller && (
                      <Button variant="outline" onClick={() => setSignDialog({ open: true, intervention: it, signer: 'technician' })} data-testid={`sign-tech-${it.id}`} className="text-violet-700">
                        <Signature size={16} className="mr-1" /> Signer (technicien)
                      </Button>
                    )}
                    {!isSeller && (
                      <Button variant="outline" onClick={() => openPhoto(it)} data-testid={`photo-bon-${it.id}`} className="text-amber-700">
                        <Camera size={16} className="mr-1" /> Photo bon signé
                      </Button>
                    )}
                    {!isTech && (
                      <Button variant="outline" onClick={() => openEdit(it)} data-testid={`edit-intervention-${it.id}`}>
                        <PencilSimple size={16} className="mr-1" /> Modifier
                      </Button>
                    )}
                    {isTech && (
                      <Button variant="outline" onClick={() => openEdit(it)} data-testid={`edit-tech-${it.id}`}>
                        <PencilSimple size={16} className="mr-1" /> Date / Observation
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="outline" onClick={() => handleDelete(it)} data-testid={`delete-intervention-${it.id}`} className="text-red-600 hover:bg-red-50">
                        <Trash size={16} className="mr-1" /> Supprimer
                      </Button>
                    )}
                  </div>
                </div>

                {it.signed_photo && (
                  <div className="mt-4 border-t border-slate-100 pt-3">
                    <p className="text-xs text-slate-500 mb-2">Photo du bon signé :</p>
                    <a href={it.signed_photo} target="_blank" rel="noopener noreferrer">
                      <img src={it.signed_photo} alt="Bon signé" className="max-h-40 rounded border border-slate-200" data-testid={`signed-photo-${it.id}`} />
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create / Edit dialog */}
        <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) resetForm(); }}>
          <DialogContent data-testid="intervention-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Modifier l'intervention" : 'Nouvelle intervention'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isTech && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
                  <select
                    value={form.client_id}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                    data-testid="intervention-client-select"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                    required
                  >
                    <option value="">— Sélectionner un client —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{c.phone ? ` — ${c.phone}` : ''}</option>
                    ))}
                  </select>
                  {form.client_id && (() => {
                    const c = clients.find((x) => x.id === form.client_id);
                    if (!c) return null;
                    return (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-slate-700 space-y-1">
                        {c.address && <div><MapPin size={12} className="inline mr-1" />{c.address}</div>}
                        {c.phone && <div><Phone size={12} className="inline mr-1" />{c.phone}</div>}
                        {c.email && <div><EnvelopeSimple size={12} className="inline mr-1" />{c.email}</div>}
                      </div>
                    );
                  })()}
                </div>
              )}
              {!isTech && form.invoice_id && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                  Rattachée à la facture <strong>n° {form.invoice_id.slice(-6).toUpperCase()}</strong>
                </div>
              )}
              {!isTech && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Technicien affecté</label>
                  <select
                    value={form.technician_id}
                    onChange={(e) => setForm({ ...form, technician_id: e.target.value })}
                    data-testid="intervention-technician-select"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  >
                    <option value="">— Non assigné —</option>
                    {technicians.map((t) => (
                      <option key={t.id} value={t.id}>{t.full_name || t.username} ({t.role === 'admin' ? 'Admin' : 'Technicien'})</option>
                    ))}
                  </select>
                </div>
              )}
              {/* All roles (including technician) can edit scheduled_date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date d&apos;intervention</label>
                <Input
                  type="datetime-local"
                  value={form.scheduled_date}
                  onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                  data-testid="intervention-date-input"
                />
              </div>
              {!isTech && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description de la demande</label>
                  <textarea
                    value={form.request_description}
                    onChange={(e) => setForm({ ...form, request_description: e.target.value })}
                    data-testid="intervention-description-input"
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Détaillez la demande du client..."
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Observation</label>
                <textarea
                  value={form.observation}
                  onChange={(e) => setForm({ ...form, observation: e.target.value })}
                  data-testid="intervention-observation-input"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Compte-rendu, pièces utilisées, durée, etc."
                />
              </div>
              {!isSeller && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded">
                  <input
                    type="checkbox"
                    id="intervention-done"
                    checked={form.done}
                    onChange={(e) => setForm({ ...form, done: e.target.checked })}
                    data-testid="intervention-done-checkbox"
                  />
                  <label htmlFor="intervention-done" className="text-sm text-slate-800 font-medium">Intervention effectuée</label>
                </div>
              )}
              {isSeller && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                  <strong>Note :</strong> Seul un administrateur ou un technicien peut marquer une intervention comme effectuée.
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
                <Button type="submit" data-testid="submit-intervention-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
                  {editing ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Signature dialog */}
        <Dialog open={signDialog.open} onOpenChange={(o) => !o && setSignDialog({ open: false, intervention: null, signer: 'client' })}>
          <DialogContent data-testid="signature-dialog" className="max-w-xl">
            <DialogHeader>
              <DialogTitle>
                Signature {signDialog.signer === 'client' ? 'du client' : 'du technicien'}
                {signDialog.intervention?.client_name && ` — ${signDialog.intervention.client_name}`}
              </DialogTitle>
            </DialogHeader>
            <SignaturePad
              onSave={saveSignature}
              onCancel={() => setSignDialog({ open: false, intervention: null, signer: 'client' })}
            />
          </DialogContent>
        </Dialog>

        {/* Photo dialog */}
        <Dialog open={photoDialog.open} onOpenChange={(o) => !o && setPhotoDialog({ open: false, intervention: null })}>
          <DialogContent data-testid="photo-dialog">
            <DialogHeader>
              <DialogTitle>Photo du bon signé</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Prenez en photo le bon d&apos;intervention signé par le client pour l&apos;archiver. Choisissez d&apos;utiliser l&apos;appareil photo (recommandé sur mobile) ou de sélectionner une image existante depuis la galerie.</p>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onPhotoSelected}
                data-testid="photo-camera-input"
                className="hidden"
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                onChange={onPhotoSelected}
                data-testid="photo-gallery-input"
                className="hidden"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Button type="button" onClick={() => cameraInputRef.current?.click()} data-testid="photo-camera-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
                  <Camera size={18} className="mr-2" /> Prendre une photo
                </Button>
                <Button type="button" variant="outline" onClick={() => galleryInputRef.current?.click()} data-testid="photo-gallery-btn">
                  <ImageIcon size={18} className="mr-2" /> Choisir depuis la galerie
                </Button>
              </div>
              <p className="text-xs text-slate-500">Sur mobile, « Prendre une photo » ouvre directement l&apos;appareil photo. Sur ordinateur (sans webcam), utilisez plutôt « Choisir depuis la galerie ».</p>
              {photoDialog.intervention?.signed_photo && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Photo actuelle :</p>
                  <img src={photoDialog.intervention.signed_photo} alt="Bon signé actuel" className="max-h-60 rounded border border-slate-200" />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Interventions;
